<script setup lang="ts">
interface Apartment {
  area_sqm?: number;
  living_sqm?: number;
  terrace_sqm?: number;
  view_type?: "sea" | "panoramic" | "mountain";
  view_details?: string;
  floor?: number | string;
  capacity?: number | string;
  min_stay_nights?: number;
  extra_bed_available?: boolean;
  amenities?: {
    bedroom?: string[];
    kitchen?: string[];
    bath?: string[];
    comfort?: string[];
    outdoor?: string[];
    extras?: string[];
  };
  weekly_includes?: string[];
}

const props = defineProps<{
  apartment: Apartment;
}>();

const { t, n } = useI18n();

const hasFacts = computed(
  () =>
    !!(
      props.apartment.area_sqm ||
      props.apartment.view_details ||
      props.apartment.min_stay_nights ||
      (props.apartment.amenities &&
        Object.values(props.apartment.amenities).some((g) => g && g.length)) ||
      (props.apartment.weekly_includes && props.apartment.weekly_includes.length)
    )
);

const viewLabel = computed(() => {
  const type = props.apartment.view_type;
  if (!type) return "";
  return t(`apartment.facts.view.${type}`);
});

const { floorNum, isHighFloor } = useApartmentFacts(
  () => props.apartment,
  () => null,
);

const amenityGroups = computed(() => {
  const g = props.apartment.amenities ?? {};
  return (['bedroom', 'kitchen', 'bath', 'comfort', 'outdoor', 'extras'] as const)
    .map((key) => ({ key, items: g[key] ?? [] }))
    .filter(({ items }) => items.length > 0);
});
const hasAmenityGroups = computed(() => amenityGroups.value.length > 0);
</script>

<template>
  <section v-if="hasFacts" class="mt-10">
    <div
      class="flex flex-wrap items-baseline gap-x-3 gap-y-2 font-body text-xs uppercase tracking-[0.22em] text-slate-400 dark:text-slate-500"
    >
      <span class="inline-block h-px w-8 bg-amber-400" />
      <span>{{ t("apartment.facts.eyebrow") }}</span>
    </div>

    <h3
      class="mt-3 font-display text-2xl italic text-slate-900 sm:text-3xl md:text-4xl dark:text-white"
    >
      {{ t("apartment.facts.heading") }}
    </h3>

    <div class="mt-6 grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-4">
      <div
        v-if="apartment.area_sqm"
        class="rounded-lg border border-slate-200/80 bg-white/50 p-4 dark:border-slate-700/60 dark:bg-slate-800/40"
      >
        <div
          class="font-body text-[11px] uppercase tracking-[0.2em] text-slate-400 dark:text-slate-500"
        >
          {{ t("apartment.facts.area") }}
        </div>
        <div class="mt-1 flex items-baseline gap-1">
          <span
            class="font-display text-3xl sm:text-4xl font-semibold italic tabular-nums text-slate-900 dark:text-white"
            >{{ n(apartment.area_sqm) }}</span
          >
          <span class="text-xs text-slate-500 dark:text-slate-400">m²</span>
        </div>
        <div
          v-if="apartment.living_sqm && apartment.terrace_sqm"
          class="mt-1.5 font-body text-[11px] leading-snug text-slate-500 dark:text-slate-400"
        >
          {{ Math.round(apartment.living_sqm) }} {{ t('apartment.facts.living') }}
          <span class="text-slate-400 dark:text-slate-500">·</span>
          {{ Math.round(apartment.terrace_sqm) }} {{ t('apartment.facts.terrace') }}
        </div>
      </div>

      <div
        v-if="viewLabel"
        class="rounded-lg border border-amber-400/40 bg-amber-400/5 p-4"
      >
        <div
          class="font-body text-[11px] uppercase tracking-[0.2em] text-amber-600 dark:text-amber-400"
        >
          {{ t("apartment.facts.view.label") }}
        </div>
        <div class="mt-1 font-display text-lg italic text-slate-900 dark:text-white">
          {{ viewLabel }}
        </div>
        <div
          v-if="apartment.view_details"
          class="mt-1 text-xs text-slate-500 dark:text-slate-400"
        >
          {{ apartment.view_details }}
        </div>
      </div>

      <div
        v-if="apartment.capacity"
        class="rounded-lg border border-slate-200/80 bg-white/50 p-4 dark:border-slate-700/60 dark:bg-slate-800/40"
      >
        <div
          class="font-body text-[11px] uppercase tracking-[0.2em] text-slate-400 dark:text-slate-500"
        >
          {{ t("apartment.facts.capacity") }}
        </div>
        <div class="mt-1 flex items-baseline gap-1">
          <span
            class="font-display text-3xl sm:text-4xl font-semibold italic tabular-nums text-slate-900 dark:text-white"
            >{{ apartment.capacity }}</span
          >
          <span class="text-xs text-slate-500 dark:text-slate-400">{{
            t("apartment.facts.guests")
          }}</span>
        </div>
        <div
          v-if="apartment.extra_bed_available"
          class="mt-1 text-xs text-amber-600 dark:text-amber-400"
        >
          {{ t("apartment.facts.extra_bed") }}
        </div>
      </div>

      <div
        v-if="apartment.min_stay_nights"
        class="rounded-lg border border-slate-200/80 bg-white/50 p-4 dark:border-slate-700/60 dark:bg-slate-800/40"
      >
        <div
          class="font-body text-[11px] uppercase tracking-[0.2em] text-slate-400 dark:text-slate-500"
        >
          {{ t("apartment.facts.min_stay") }}
        </div>
        <div class="mt-1 flex items-baseline gap-1">
          <span
            class="font-display text-3xl sm:text-4xl font-semibold italic tabular-nums text-slate-900 dark:text-white"
            >{{ apartment.min_stay_nights }}</span
          >
          <span class="text-xs text-slate-500 dark:text-slate-400">{{
            t("apartment.facts.nights")
          }}</span>
        </div>
      </div>
    </div>

    <div
      v-if="isHighFloor && floorNum"
      class="mt-4 inline-flex items-center gap-2 rounded-full border border-amber-400/40 bg-amber-400/10 px-3 py-1.5 text-xs font-body tracking-[0.15em] uppercase whitespace-nowrap text-amber-700 dark:text-amber-300"
    >
      <svg viewBox="0 0 24 24" class="h-3 w-3" fill="currentColor">
        <path d="M12 2l1.8 5.5h5.7l-4.6 3.4 1.8 5.5L12 13l-4.7 3.4 1.8-5.5-4.6-3.4h5.7z" />
      </svg>
      {{ t("apartmentRow.badges.floor", { floor: floorNum }) }}
    </div>

    <div v-if="hasAmenityGroups" class="mt-10">
      <h4
        class="font-display text-xl italic text-slate-900 sm:text-2xl md:text-3xl dark:text-white"
      >
        {{ t("apartment.facts.amenities") }}
      </h4>
      <div v-for="group in amenityGroups" :key="group.key" class="mt-6">
        <h5
          class="font-body text-[11px] uppercase tracking-[0.2em] text-amber-600 dark:text-amber-400"
        >
          {{ t(`apartment.amenities.${group.key}`) }}
        </h5>
        <ul
          class="mt-2 grid grid-cols-1 gap-x-6 gap-y-2 font-body text-sm md:text-base text-slate-700 sm:grid-cols-2 md:grid-cols-3 dark:text-slate-300"
        >
          <li
            v-for="(item, i) in group.items"
            :key="i"
            class="flex items-start gap-2"
          >
            <svg
              viewBox="0 0 24 24"
              class="mt-1 h-3.5 w-3.5 flex-shrink-0 text-amber-500 dark:text-amber-400"
              fill="none"
              stroke="currentColor"
              stroke-width="2.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M20 6L9 17l-5-5" />
            </svg>
            <span>{{ item }}</span>
          </li>
        </ul>
      </div>
    </div>

    <div
      v-if="apartment.weekly_includes && apartment.weekly_includes.length"
      class="relative mt-10 overflow-hidden rounded-xl border border-amber-400/40 bg-gradient-to-br from-amber-400/10 via-amber-400/5 to-transparent p-6 md:p-8"
    >
      <div
        aria-hidden="true"
        class="absolute top-0 right-0 h-24 w-24 -translate-y-8 translate-x-8 rounded-full bg-amber-400/20 blur-3xl"
      />
      <div class="relative">
        <div
          class="font-body text-[11px] uppercase tracking-[0.25em] text-amber-600 dark:text-amber-400"
        >
          {{ t("apartment.facts.weekly.eyebrow") }}
        </div>
        <h4
          class="mt-2 font-display text-2xl italic text-slate-900 sm:text-3xl md:text-4xl dark:text-white"
        >
          {{ t("apartment.facts.weekly.heading") }}
        </h4>
        <ul
          class="mt-5 grid grid-cols-1 gap-3 font-body text-sm md:text-base text-slate-800 md:grid-cols-3 dark:text-slate-100"
        >
          <li
            v-for="(perk, i) in apartment.weekly_includes"
            :key="i"
            class="flex items-start gap-2"
          >
            <svg
              viewBox="0 0 24 24"
              class="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-500 dark:text-amber-400"
              fill="currentColor"
            >
              <path
                d="M12 2l2.4 7.4H22l-6.2 4.5 2.4 7.4L12 16.8 5.8 21.3l2.4-7.4L2 9.4h7.6z"
              />
            </svg>
            <span class="font-medium">{{ perk }}</span>
          </li>
        </ul>
      </div>
    </div>
  </section>
</template>
